import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useEffect, useId, useMemo, useRef, useState } from 'react';

import {
  findGenerationPreset,
  GENERATION_ASPECTS,
  GENERATION_PRESETS,
  GENERATION_PURPOSES,
  type GenerationAspect,
  type GenerationAuthStyle,
  type GenerationConnectorSlot,
  type GenerationPreset,
  type GenerationPurpose,
  type GenerationQualityTier,
  type GenerationResponseKind,
  type GenerationVideoInput,
} from '@mpr/shared';

import type {
  GenerationCapability,
  GenerationConnector,
  GenerationConnectorInput,
  GenerationConnectorTest,
  GenerationConnectorVerification,
} from '../lib/api.js';
import { api, ApiError } from '../lib/api.js';
import { useI18n } from '../i18n/LanguageProvider.js';
import type { TranslationKey } from '../i18n/translations.js';
import { CatalogueRefresh } from './CatalogueRefresh.js';
import { ConnectorRepairAssistant } from './ConnectorRepairAssistant.js';
import { Icon } from './Icons.js';
import { ConnectorPricingFields, PriceTag } from './ConnectorPricingFields.js';
import { pricingFormFrom, pricingFromForm } from '../lib/pricing.js';
import { ProviderResponseTree, type PathsInEffect, type PathTargetId } from './ProviderResponseTree.js';
import type { ContractField, ContractSnapshot } from '../lib/catalogueDiff.js';

const CAPABILITIES: GenerationCapability[] = ['SCRIPT', 'AUDIO', 'VIDEO', 'IMAGE'];
const AUTH_STYLES: GenerationAuthStyle[] = ['BEARER', 'HEADER', 'QUERY', 'NONE'];

/** Columns in the table; the in-place edit row spans them all. */
const COLUMN_COUNT = 11;

const VIDEO_INPUTS: GenerationVideoInput[] = ['TEXT', 'IMAGE'];
const QUALITY_TIERS: GenerationQualityTier[] = ['DRAFT', 'STANDARD', 'HIGH'];

/** The slot fields a preset may carry; the catalogue gains them without the type changing yet. */
type PresetSlots = Partial<GenerationConnectorSlot>;

/**
 * Presets that are catalogued under IMAGE but produce any media — the
 * platform's own media contract and Hugging Face raw bytes. Listing every
 * non-text preset under every media capability would offer "OpenAI — image"
 * as a video provider, which an operator would reasonably believe.
 */
const ANY_MEDIA_PRESETS = new Set(['mpr-media', 'huggingface-media']);

function responseKindsFor(capability: GenerationCapability): GenerationResponseKind[] {
  return capability === 'SCRIPT' ? ['TEXT'] : ['URL', 'BASE64', 'BINARY'];
}

function presetsFor(capability: GenerationCapability): GenerationPreset[] {
  return GENERATION_PRESETS.filter(
    (preset) =>
      preset.capability === capability ||
      (capability !== 'SCRIPT' && ANY_MEDIA_PRESETS.has(preset.id)),
  );
}

interface FormState {
  name: string;
  capability: GenerationCapability;
  provider: string;
  endpointUrl: string;
  authToken: string;
  isDefault: boolean;
  /** '' is "custom". */
  presetId: string;
  authStyle: GenerationAuthStyle;
  authParamName: string;
  model: string;
  /** The JSON as typed; parsed only on save. */
  requestTemplate: string;
  responsePath: string;
  /** '' only for a legacy row saved before connectors carried a contract. */
  responseKind: GenerationResponseKind | '';
  responseMimePath: string;
  /** Polling trio — all empty means the first reply is the answer. */
  pollPath: string;
  pollUrlTemplate: string;
  pollDonePath: string;
  authOnResultFetch: boolean;
  /** Status words, for a done path that is not a boolean. */
  pollDoneValue: string;
  pollFailedValues: string;
  /** Fixed headers as typed JSON; parsed only on save. */
  extraHeaders: string;
  /** Where it is used. Every list is a filter: empty means any. */
  purposes: GenerationPurpose[];
  videoInputs: GenerationVideoInput[];
  aspectRatios: GenerationAspect[];
  /** '' is "any". */
  qualityTier: GenerationQualityTier | '';
  /** Typed; a whole number of seconds, or empty for unknown. */
  maxClipSeconds: string;
  supportsExtension: boolean;
}

/** The slot fields as the form holds them, from a row or a preset (which may not carry them). */
function slotsToForm(source: PresetSlots): Pick<
  FormState,
  'purposes' | 'videoInputs' | 'aspectRatios' | 'qualityTier' | 'maxClipSeconds' | 'supportsExtension'
> {
  return {
    purposes: source.purposes ?? [],
    videoInputs: source.videoInputs ?? [],
    aspectRatios: source.aspectRatios ?? [],
    qualityTier: source.qualityTier ?? '',
    maxClipSeconds: source.maxClipSeconds ? String(source.maxClipSeconds) : '',
    supportsExtension: source.supportsExtension ?? false,
  };
}

/** Add the value when absent, remove it when present — for the checkboxes and chips. */
function toggled<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((entry) => entry !== value) : [...list, value];
}

/** A whole number of seconds, or null when blank or not a positive number. */
function parseSeconds(text: string): number | null {
  const n = Number(text.trim());
  return text.trim() !== '' && Number.isInteger(n) && n > 0 ? n : null;
}

/** Which form field a tapped path is written into. */
const PATH_FIELD: Partial<Record<string, ContractField>> = {
  result: 'responsePath',
  operation: 'pollPath',
  done: 'pollDonePath',
  mime: 'responseMimePath',
};

const blankForm: FormState = {
  name: '',
  capability: 'SCRIPT',
  provider: '',
  endpointUrl: '',
  authToken: '',
  isDefault: false,
  presetId: '',
  authStyle: 'BEARER',
  authParamName: '',
  model: '',
  requestTemplate: '',
  responsePath: '',
  responseKind: 'TEXT',
  responseMimePath: '',
  pollPath: '',
  pollUrlTemplate: '',
  pollDonePath: '',
  authOnResultFetch: false,
  pollDoneValue: '',
  pollFailedValues: '',
  extraHeaders: '',
  ...slotsToForm({}),
};

/** The Veo preset is the one worked example of polling; its values are the placeholders. */
const POLL_EXAMPLE = {
  pollPath: 'name',
  pollUrlTemplate: 'https://generativelanguage.googleapis.com/v1beta/{{operation}}',
  pollDonePath: 'done',
};

function prettyTemplate(template: unknown): string {
  return template === null || template === undefined ? '' : JSON.stringify(template, null, 2);
}

function formFromRow(row: GenerationConnector): FormState {
  return {
    name: row.name,
    capability: row.capability,
    provider: row.provider,
    endpointUrl: row.endpointUrl,
    authToken: '',
    isDefault: row.isDefault,
    presetId: row.presetId ?? '',
    authStyle: row.authStyle,
    authParamName: row.authParamName ?? '',
    model: row.model ?? '',
    requestTemplate: prettyTemplate(row.requestTemplate),
    responsePath: row.responsePath ?? '',
    responseKind: row.responseKind ?? '',
    responseMimePath: row.responseMimePath ?? '',
    pollPath: row.pollPath ?? '',
    pollUrlTemplate: row.pollUrlTemplate ?? '',
    pollDonePath: row.pollDonePath ?? '',
    authOnResultFetch: row.authOnResultFetch,
    pollDoneValue: row.pollDoneValue ?? '',
    pollFailedValues: row.pollFailedValues ?? '',
    extraHeaders: prettyTemplate(row.extraHeaders),
    ...slotsToForm(row),
  };
}

type TemplateCheck = { ok: true; value: unknown } | { ok: false; message: string };

/** Extra headers: blank, or a JSON object whose every value is text. */
function parseHeaders(text: string): Record<string, string> | null | undefined {
  if (text.trim() === '') return null;
  try {
    const value: unknown = JSON.parse(text);
    if (typeof value !== 'object' || value === null || Array.isArray(value)) return undefined;
    if (!Object.values(value).every((entry) => typeof entry === 'string')) return undefined;
    return value as Record<string, string>;
  } catch {
    return undefined;
  }
}

function parseTemplate(text: string): TemplateCheck {
  if (text.trim() === '') return { ok: true, value: null };
  try {
    const value: unknown = JSON.parse(text);
    if (typeof value !== 'object' || value === null || Array.isArray(value)) {
      return { ok: false, message: '' };
    }
    return { ok: true, value };
  } catch (err) {
    return { ok: false, message: err instanceof Error ? err.message : String(err) };
  }
}

function nullable(value: string): string | null {
  const trimmed = value.trim();
  return trimmed === '' ? null : trimmed;
}

const VERIFICATION_BADGE: Record<GenerationConnectorVerification, { className: string; label: TranslationKey }> = {
  UNTESTED: { className: 'warn', label: 'studioConnectors.verification.UNTESTED' },
  PASSED: { className: 'ok', label: 'studioConnectors.verification.PASSED' },
  FAILED: { className: 'err', label: 'studioConnectors.verification.FAILED' },
};

function VerificationBadge({ status }: { status: GenerationConnectorVerification }) {
  const { t } = useI18n();
  const badge = VERIFICATION_BADGE[status];
  return <span className={`badge ${badge.className}`}>{t(badge.label)}</span>;
}

/** What a connector is meant for, as small tags; "any use" when it declares nothing. */
function SlotTags({ connector }: { connector: GenerationConnector }) {
  const { t } = useI18n();
  const tags: string[] = [
    ...(connector.purposes ?? []).map((p) => t(`studioConnectors.slot.purpose.${p}` as TranslationKey)),
    ...(connector.videoInputs ?? []).map((i) => t(`studioConnectors.slot.videoInput.${i}` as TranslationKey)),
    ...(connector.aspectRatios ?? []),
    ...(connector.qualityTier ? [t(`studio.opt.quality.${connector.qualityTier}` as TranslationKey)] : []),
    ...(connector.maxClipSeconds
      ? [t('studioConnectors.tag.maxClip', { seconds: connector.maxClipSeconds })]
      : []),
    ...(connector.supportsExtension ? [t('studioConnectors.tag.extension')] : []),
  ];
  if (tags.length === 0) return <span className="muted tiny">{t('studioConnectors.slot.none')}</span>;
  return (
    <div className="connector-tags">
      {tags.map((tag, index) => (
        <span key={`${tag}-${index}`} className="badge tiny">
          {tag}
        </span>
      ))}
    </div>
  );
}

/** A test result's file or text, shown the way the studio shows a job's. */
function TestResult({ test, capability }: { test: GenerationConnectorTest; capability: GenerationCapability }) {
  const { t } = useI18n();
  if (test.resultText) {
    return (
      <pre className="connector-test-text" dir="auto">
        {test.resultText}
      </pre>
    );
  }
  if (!test.resultMediaId) return null;
  // The session route the studio itself plays results from.
  const src = `/api/media/${test.resultMediaId}`;
  const mime = test.resultMimeType ?? '';
  if (capability === 'IMAGE' || mime.startsWith('image/')) {
    return <img className="connector-test-media" src={src} alt={t('studioConnectors.test.resultImage')} />;
  }
  if (capability === 'AUDIO' || mime.startsWith('audio/')) {
    return <audio className="connector-test-media" src={src} controls />;
  }
  return <video className="connector-test-media" src={src} controls />;
}

/**
 * Runs a real generation on a saved connector and shows what happened: the
 * result, the error if it failed, and — always — the provider's own reply,
 * so the response path can be written from what actually came back instead
 * of guessed. A connector is only usable once this has passed.
 *
 * The test is asynchronous on the server (a video takes minutes): this posts,
 * then polls the connector's test state until it is over.
 */
function ConnectorTestPanel({
  connector,
  paths,
  onUsePath,
  onRepaired,
}: {
  connector: GenerationConnector;
  /** The paths now in the form (edit mode), to mark where they land in the reply. */
  paths?: PathsInEffect;
  /** Edit mode: write a tapped path into the form. Absent in the stand-alone test row. */
  onUsePath?: (target: PathTargetId, path: string) => void;
  /** Edit mode: an auto-fix saved the connector, so the form should follow it. */
  onRepaired?: (connector: GenerationConnector) => void;
}) {
  const { t } = useI18n();
  const queryClient = useQueryClient();
  const uid = useId();
  const [prompt, setPrompt] = useState('');
  const [reference, setReference] = useState<{ id: string; name: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const takesReference = connector.capability === 'VIDEO' || connector.capability === 'IMAGE';

  const stateQuery = useQuery({
    queryKey: ['generation-connector-test', connector.id],
    queryFn: () => api.getGenerationConnectorTest(connector.id),
    refetchInterval: (query) => (query.state.data?.test?.running ? 3000 : false),
  });
  const state = stateQuery.data;
  const test = state?.test ?? null;
  const running = Boolean(test?.running);

  // The badge in the table follows the verdict: refresh the list when a run ends.
  const previousStatus = useRef<string | null>(null);
  useEffect(() => {
    const status = test?.status ?? null;
    if (previousStatus.current && previousStatus.current !== status && !running) {
      void queryClient.invalidateQueries({ queryKey: ['generation-connectors'] });
    }
    previousStatus.current = status;
  }, [test?.status, running, queryClient]);

  const upload = useMutation({
    mutationFn: (file: File) => api.uploadGenerationMedia(file),
    onSuccess: ({ asset }) => setReference({ id: asset.id, name: asset.filename }),
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.message : t('common.requestFailed')),
  });

  const start = useMutation({
    mutationFn: () =>
      api.testGenerationConnector(connector.id, {
        ...(prompt.trim() ? { prompt: prompt.trim() } : {}),
        ...(reference ? { referenceMediaIds: [reference.id] } : {}),
      }),
    onSuccess: () => {
      setError(null);
      void queryClient.invalidateQueries({ queryKey: ['generation-connector-test', connector.id] });
    },
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.message : t('common.requestFailed')),
  });

  const run = () => {
    // A video is billed per second; one click must not spend money unannounced.
    if (connector.capability === 'VIDEO' && !window.confirm(t('studioConnectors.test.videoCost'))) return;
    start.mutate();
  };

  const failedMessage =
    test && !test.running && test.status === 'FAILED'
      ? test.errorMessage
      : connector.verificationStatus === 'FAILED' && !test
        ? connector.lastTestError
        : null;

  return (
    <div className="connector-test">
      <h4>{t('studioConnectors.test.title')}</h4>
      <p className="muted small">{t('studioConnectors.test.hint')}</p>

      <label htmlFor={`connector-test-prompt-${uid}`}>{t('studioConnectors.test.prompt')}</label>
      <textarea
        id={`connector-test-prompt-${uid}`}
        dir="auto"
        rows={2}
        placeholder={t('studioConnectors.test.promptPlaceholder')}
        value={prompt}
        onChange={(event) => setPrompt(event.target.value)}
        disabled={running}
      />

      {takesReference && (
        <>
          <label htmlFor={`connector-test-ref-${uid}`}>{t('studioConnectors.test.reference')}</label>
          <input
            id={`connector-test-ref-${uid}`}
            type="file"
            accept="image/*"
            disabled={running || upload.isPending}
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) upload.mutate(file);
            }}
          />
          {reference && <p className="muted tiny">{reference.name}</p>}
        </>
      )}

      {connector.capability === 'VIDEO' && (
        <p className="warn-text small">{t('studioConnectors.test.videoCostNote')}</p>
      )}

      <div className="actions">
        <button type="button" className="primary" onClick={run} disabled={running || start.isPending}>
          {running ? t('studioConnectors.test.running') : t('studioConnectors.test.run')}
        </button>
        <VerificationBadge status={connector.verificationStatus} />
        {connector.lastTestedAt && (
          <span className="muted tiny">
            {t('studioConnectors.test.lastTested', {
              when: new Date(connector.lastTestedAt).toLocaleString(),
            })}
          </span>
        )}
      </div>

      {running && (
        <p className="muted small" role="status" aria-live="polite">
          {connector.capability === 'VIDEO'
            ? t('studioConnectors.test.runningVideo')
            : t('studioConnectors.test.runningShort')}
        </p>
      )}
      {error && <p className="error small">{error}</p>}
      {failedMessage && (
        <p className="error small" dir="auto">
          {t('studioConnectors.test.failed', { error: failedMessage })}
        </p>
      )}

      <ConnectorRepairAssistant
        connector={connector}
        failed={Boolean(failedMessage)}
        testInput={{
          ...(prompt.trim() ? { prompt: prompt.trim() } : {}),
          ...(reference ? { referenceMediaIds: [reference.id] } : {}),
        }}
        onSaved={onRepaired}
      />

      {test && !test.running && test.status === 'DONE' && (
        <div className="connector-test-result">
          <p className="ok-text small">{t('studioConnectors.test.passed')}</p>
          <TestResult test={test} capability={connector.capability} />
        </div>
      )}

      {state?.providerRequest && (
        <details className="connector-test-response">
          <summary>{t('studioConnectors.test.providerRequest')}</summary>
          <p className="muted tiny">{t('studioConnectors.test.providerRequestHint')}</p>
          <pre className="ltr mono tiny" dir="ltr">
            {state.providerRequest}
          </pre>
        </details>
      )}
      {state?.providerResponse && (
        <details className="connector-test-response">
          <summary>{t('studioConnectors.test.providerResponse')}</summary>
          <p className="muted tiny">{t('studioConnectors.test.providerResponseHint')}</p>
          <pre className="ltr mono tiny" dir="ltr">
            {state.providerResponse}
          </pre>
          <details className="connector-test-pick" open={Boolean(onUsePath)}>
            <summary>{t('studioConnectors.tree.title')}</summary>
            <ProviderResponseTree
              raw={state.providerResponse}
              paths={
                paths ?? {
                  result: connector.responsePath,
                  operation: connector.pollPath,
                  done: connector.pollDonePath,
                  mime: connector.responseMimePath,
                }
              }
              onUse={onUsePath}
              extraTargets={
                onUsePath
                  ? [
                      { id: 'usageInputTokensPath', label: t('studioConnectors.pricing.usageInput') },
                      { id: 'usageOutputTokensPath', label: t('studioConnectors.pricing.usageOutput') },
                      { id: 'usageSecondsPath', label: t('studioConnectors.pricing.usageSeconds') },
                    ]
                  : []
              }
            />
          </details>
        </details>
      )}
    </div>
  );
}

interface ConnectorFormProps {
  mode: 'create' | 'edit';
  /** The row being edited; absent for a new connector. */
  initial?: GenerationConnector;
  onSaved: () => void;
  onCancel?: () => void;
}

/**
 * The add/edit form. One component for both so a field added later shows up
 * in both places; the only differences are the seed, the save verb, and where
 * it is rendered (beneath its row for edit, below the table for create).
 * Same "blank credential means keep what is stored" rule on edit as Agents.
 */
function ConnectorForm({ mode, initial, onSaved, onCancel }: ConnectorFormProps) {
  const { t } = useI18n();
  // Both forms can be on the page at once, so ids must not collide.
  const uid = useId();
  const id = (name: string) => `connector-${name}-${uid}`;

  const [form, setForm] = useState<FormState>(() => (initial ? formFromRow(initial) : blankForm));
  const [error, setError] = useState<string | null>(null);
  // Until the operator picks a capability themselves, a preset may set it.
  // Editing an existing row counts as having picked one.
  const [capabilityTouched, setCapabilityTouched] = useState(mode === 'edit');
  const [advancedOpen, setAdvancedOpen] = useState(mode === 'edit' ? !initial?.presetId : true);
  // Price and usage paths (accounting only), kept beside the contract form so a preset can fill them.
  const [pricingForm, setPricingForm] = useState(() => pricingFormFrom(initial));
  const pricingResult = useMemo(() => pricingFromForm(pricingForm), [pricingForm]);

  const template = useMemo(() => parseTemplate(form.requestTemplate), [form.requestTemplate]);
  const extraHeaders = useMemo(() => parseHeaders(form.extraHeaders), [form.extraHeaders]);
  const preset = form.presetId ? findGenerationPreset(form.presetId) : undefined;
  const responseKinds = responseKindsFor(form.capability);
  const isMedia = form.capability === 'VIDEO' || form.capability === 'IMAGE';
  const clipInvalid = form.maxClipSeconds.trim() !== '' && parseSeconds(form.maxClipSeconds) === null;

  /** The contract as the form holds it now, for the catalogue comparison. */
  const contractNow: ContractSnapshot = {
    endpointUrl: form.endpointUrl.trim(),
    authStyle: form.authStyle,
    authParamName: nullable(form.authParamName),
    model: nullable(form.model),
    requestTemplate: template.ok ? template.value : null,
    responsePath: nullable(form.responsePath),
    responseKind: form.responseKind || null,
    responseMimePath: nullable(form.responseMimePath),
    pollPath: nullable(form.pollPath),
    pollUrlTemplate: nullable(form.pollUrlTemplate),
    pollDonePath: nullable(form.pollDonePath),
    authOnResultFetch: form.authOnResultFetch,
    extraHeaders: extraHeaders ?? null,
    pollDoneValue: nullable(form.pollDoneValue),
    pollFailedValues: nullable(form.pollFailedValues),
  };

  /** A path tapped in the provider's reply goes into the matching field. */
  const usePath = (target: PathTargetId, path: string) => {
    if (target === 'usageInputTokensPath' || target === 'usageOutputTokensPath' || target === 'usageSecondsPath') {
      setPricingForm((current) => ({ ...current, [target]: path }));
      setAdvancedOpen(true);
      return;
    }
    const field = PATH_FIELD[target];
    if (!field) return;
    setForm((current) => ({ ...current, [field]: path }));
    setAdvancedOpen(true);
  };

  const save = useMutation({
    mutationFn: async (): Promise<void> => {
      if (!template.ok || extraHeaders === undefined) return;
      const needsParam = form.authStyle === 'HEADER' || form.authStyle === 'QUERY';
      const body: GenerationConnectorInput = {
        name: form.name,
        capability: form.capability,
        provider: form.provider,
        endpointUrl: form.endpointUrl.trim(),
        // Blank means "keep what is stored" — the token is shown once, on
        // entry, and never read back.
        authToken: form.authToken || undefined,
        // A connector can only become the default once it has passed its test,
        // so a new one never asks, and an edit asks only when the box changed.
        ...(mode === 'edit' && initial && form.isDefault !== initial.isDefault
          ? { isDefault: form.isDefault }
          : {}),
        presetId: nullable(form.presetId),
        authStyle: form.authStyle,
        authParamName: needsParam ? nullable(form.authParamName) : null,
        model: nullable(form.model),
        requestTemplate: template.value,
        responsePath: form.responseKind === 'BINARY' ? null : nullable(form.responsePath),
        responseKind: form.responseKind || null,
        responseMimePath: form.responseKind === 'BASE64' ? nullable(form.responseMimePath) : null,
        pollPath: nullable(form.pollPath),
        pollUrlTemplate: nullable(form.pollUrlTemplate),
        pollDonePath: nullable(form.pollDonePath),
        authOnResultFetch: form.authOnResultFetch,
        pollDoneValue: nullable(form.pollDoneValue),
        pollFailedValues: nullable(form.pollFailedValues),
        extraHeaders,
        // Only the slot fields that make sense for this kind are sent; the
        // rest are cleared so switching the kind leaves nothing stale behind.
        purposes: form.capability === 'SCRIPT' ? form.purposes : [],
        videoInputs: form.capability === 'VIDEO' ? form.videoInputs : [],
        aspectRatios: isMedia ? form.aspectRatios : [],
        qualityTier: isMedia ? form.qualityTier || null : null,
        maxClipSeconds: form.capability === 'VIDEO' ? parseSeconds(form.maxClipSeconds) : null,
        supportsExtension: form.capability === 'VIDEO' ? form.supportsExtension : false,
        ...pricingResult.body,
      };
      if (mode === 'edit' && initial) await api.updateGenerationConnector(initial.id, body);
      else await api.createGenerationConnector(body);
    },
    onSuccess: () => {
      setError(null);
      if (mode === 'create') {
        setForm(blankForm);
        setPricingForm(pricingFormFrom(null));
        setCapabilityTouched(false);
        setAdvancedOpen(true);
      }
      onSaved();
    },
    onError: (err: unknown) =>
      setError(err instanceof ApiError ? err.message : t('common.requestFailed')),
  });

  const choosePreset = (presetId: string) => {
    const next = presetId ? findGenerationPreset(presetId) : undefined;
    const previous = form.presetId ? findGenerationPreset(form.presetId) : undefined;
    // Replace the name only while it is still the one a preset put there.
    const nameIsDefault = form.name === '' || form.name === previous?.label;
    if (!next) {
      setForm({
        ...form,
        name: nameIsDefault ? '' : form.name,
        presetId: '',
        provider: '',
        endpointUrl: '',
        authStyle: 'BEARER',
        authParamName: '',
        model: '',
        requestTemplate: '',
        responsePath: '',
        responseKind: responseKindsFor(form.capability)[0] ?? '',
        responseMimePath: '',
        pollPath: '',
        pollUrlTemplate: '',
        pollDonePath: '',
        authOnResultFetch: false,
        pollDoneValue: '',
        pollFailedValues: '',
        extraHeaders: '',
        ...slotsToForm({}),
      });
      setPricingForm(pricingFormFrom(null));
      setAdvancedOpen(true);
      return;
    }
    const capability = capabilityTouched ? form.capability : next.capability;
    const allowed = responseKindsFor(capability);
    setForm({
      ...form,
      name: nameIsDefault ? next.label : form.name,
      capability,
      presetId: next.id,
      provider: next.provider,
      endpointUrl: next.endpointUrl,
      authStyle: next.authStyle,
      authParamName: next.authParamName ?? '',
      model: next.model ?? '',
      requestTemplate: prettyTemplate(next.requestTemplate),
      responsePath: next.responsePath ?? '',
      responseKind:
        next.responseKind && allowed.includes(next.responseKind)
          ? next.responseKind
          : (allowed[0] ?? ''),
      responseMimePath: next.responseMimePath ?? '',
      pollPath: next.pollPath ?? '',
      pollUrlTemplate: next.pollUrlTemplate ?? '',
      pollDonePath: next.pollDonePath ?? '',
      authOnResultFetch: next.authOnResultFetch,
      pollDoneValue: next.pollDoneValue ?? '',
      pollFailedValues: next.pollFailedValues ?? '',
      extraHeaders: prettyTemplate(next.extraHeaders),
      // A preset may say where it is meant to be used; absent means empty (any).
      ...slotsToForm(next as GenerationPreset & PresetSlots),
    });
    setPricingForm(pricingFormFrom(next));
    setAdvancedOpen(false);
  };

  const chooseCapability = (capability: GenerationCapability) => {
    setCapabilityTouched(true);
    const allowed = responseKindsFor(capability);
    setForm({
      ...form,
      capability,
      responseKind:
        form.responseKind && allowed.includes(form.responseKind)
          ? form.responseKind
          : (allowed[0] ?? ''),
    });
  };

  const presetOption = (item: GenerationPreset) => (
    <option key={item.id} value={item.id}>
      {item.label}
    </option>
  );

  const pathPlaceholder =
    form.responseKind === 'BASE64'
      ? 'data[0].b64_json'
      : form.responseKind === 'URL'
        ? 'resultUrl'
        : 'candidates[0].content.parts[*].text';

  return (
    <>
      <label htmlFor={id('preset')}>{t('studioConnectors.preset')}</label>
      <select
        id={id('preset')}
        value={form.presetId}
        onChange={(event) => choosePreset(event.target.value)}
      >
        <option value="">{t('studioConnectors.presetCustom')}</option>
        {capabilityTouched
          ? presetsFor(form.capability).map(presetOption)
          : CAPABILITIES.map((capability) => (
              <optgroup
                key={capability}
                label={t(`studioConnectors.capability.${capability}` as TranslationKey)}
              >
                {GENERATION_PRESETS.filter((item) => item.capability === capability).map(
                  presetOption,
                )}
              </optgroup>
            ))}
      </select>
      {preset ? (
        <p className="muted small ltr">{preset.note}</p>
      ) : (
        <p className="muted small">{t('studioConnectors.presetHint')}</p>
      )}
      {mode === 'edit' && preset && (
        <CatalogueRefresh
          preset={preset}
          current={contractNow}
          capability={form.capability}
          onApply={(patch) => {
            setForm((current) => ({ ...current, ...(patch as unknown as Partial<FormState>) }));
            setAdvancedOpen(true);
          }}
        />
      )}

      <label htmlFor={id('name')}>{t('studioConnectors.name')}</label>
      <input
        id={id('name')}
        value={form.name}
        onChange={(event) => setForm({ ...form, name: event.target.value })}
      />

      <label htmlFor={id('capability')}>{t('studioConnectors.capability')}</label>
      <select
        id={id('capability')}
        value={form.capability}
        onChange={(event) => chooseCapability(event.target.value as GenerationCapability)}
      >
        {CAPABILITIES.map((capability) => (
          <option key={capability} value={capability}>
            {t(`studioConnectors.capability.${capability}` as TranslationKey)}
          </option>
        ))}
      </select>

      <label htmlFor={id('provider')}>{t('studioConnectors.provider')}</label>
      <input
        id={id('provider')}
        className="ltr"
        dir="ltr"
        value={form.provider}
        onChange={(event) => setForm({ ...form, provider: event.target.value })}
      />

      <label htmlFor={id('endpoint')}>{t('studioConnectors.endpoint')}</label>
      <input
        id={id('endpoint')}
        type="url"
        className="ltr"
        dir="ltr"
        placeholder="https://…"
        value={form.endpointUrl}
        onChange={(event) => setForm({ ...form, endpointUrl: event.target.value })}
      />

      <label htmlFor={id('token')}>{t('studioConnectors.token')}</label>
      <input
        id={id('token')}
        type="password"
        className="ltr"
        dir="ltr"
        autoComplete="off"
        value={form.authToken}
        onChange={(event) => setForm({ ...form, authToken: event.target.value })}
      />
      <p className="muted small">{t('studioConnectors.tokenHint')}</p>

      <label htmlFor={id('model')}>{t('studioConnectors.model')}</label>
      <input
        id={id('model')}
        className="ltr"
        dir="ltr"
        value={form.model}
        onChange={(event) => setForm({ ...form, model: event.target.value })}
      />
      <p className="muted small">{t('studioConnectors.modelHint')}</p>

      {mode === 'edit' && initial ? (
        <>
          <label className="check-row">
            <input
              type="checkbox"
              checked={form.isDefault}
              // Only a connector that passed its test can become the default;
              // clearing the flag is always allowed.
              disabled={!form.isDefault && initial.verificationStatus !== 'PASSED'}
              onChange={(event) => setForm({ ...form, isDefault: event.target.checked })}
            />
            <span className="tiny">{t('studioConnectors.setDefault')}</span>
          </label>
          {initial.verificationStatus !== 'PASSED' && (
            <p className="muted small">{t('studioConnectors.defaultNeedsTest')}</p>
          )}
        </>
      ) : (
        <p className="muted small">{t('studioConnectors.draftHint')}</p>
      )}

      {/* Which requests this connector is meant for. The platform picks among
          the tested connectors of a kind by these, so one can be the default
          for posts and another for video scripts. */}
      <fieldset className="connector-slots">
        <legend>{t('studioConnectors.slot.title')}</legend>
        <p className="muted small">{t('studioConnectors.slot.hint')}</p>

        {form.capability === 'SCRIPT' && (
          <div className="connector-slot-group">
            <span className="small">{t('studioConnectors.slot.purposes')}</span>
            {GENERATION_PURPOSES.map((purpose) => (
              <label key={purpose} className="check-row">
                <input
                  type="checkbox"
                  checked={form.purposes.includes(purpose)}
                  onChange={() => setForm({ ...form, purposes: toggled(form.purposes, purpose) })}
                />
                <span className="tiny">
                  {t(`studioConnectors.slot.purpose.${purpose}` as TranslationKey)}
                </span>
              </label>
            ))}
          </div>
        )}

        {form.capability === 'VIDEO' && (
          <div className="connector-slot-group">
            <span className="small">{t('studioConnectors.slot.videoInputs')}</span>
            {VIDEO_INPUTS.map((input) => (
              <label key={input} className="check-row">
                <input
                  type="checkbox"
                  checked={form.videoInputs.includes(input)}
                  onChange={() => setForm({ ...form, videoInputs: toggled(form.videoInputs, input) })}
                />
                <span className="tiny">
                  {t(`studioConnectors.slot.videoInput.${input}` as TranslationKey)}
                </span>
              </label>
            ))}
          </div>
        )}

        {isMedia && (
          <>
            <div className="connector-slot-group">
              <span className="small">{t('studioConnectors.slot.aspects')}</span>
              <div className="connector-chips">
                {GENERATION_ASPECTS.map((aspect) => {
                  const on = form.aspectRatios.includes(aspect);
                  return (
                    <button
                      key={aspect}
                      type="button"
                      className={`chip-toggle${on ? ' on' : ''}`}
                      aria-pressed={on}
                      onClick={() => setForm({ ...form, aspectRatios: toggled(form.aspectRatios, aspect) })}
                    >
                      {aspect}
                    </button>
                  );
                })}
              </div>
            </div>

            <label htmlFor={id('quality')}>{t('studioConnectors.slot.quality')}</label>
            <select
              id={id('quality')}
              value={form.qualityTier}
              onChange={(event) =>
                setForm({ ...form, qualityTier: event.target.value as GenerationQualityTier | '' })
              }
            >
              <option value="">{t('studioConnectors.slot.qualityAny')}</option>
              {QUALITY_TIERS.map((tier) => (
                <option key={tier} value={tier}>
                  {t(`studio.opt.quality.${tier}` as TranslationKey)}
                </option>
              ))}
            </select>
          </>
        )}

        {form.capability === 'VIDEO' && (
          <>
            <label htmlFor={id('max-clip')}>{t('studioConnectors.slot.maxClip')}</label>
            <input
              id={id('max-clip')}
              type="number"
              className="ltr"
              dir="ltr"
              min={1}
              step={1}
              inputMode="numeric"
              value={form.maxClipSeconds}
              onChange={(event) => setForm({ ...form, maxClipSeconds: event.target.value })}
            />
            <p className={clipInvalid ? 'error small' : 'muted small'}>{t('studioConnectors.slot.maxClipHint')}</p>

            <label className="check-row">
              <input
                type="checkbox"
                checked={form.supportsExtension}
                onChange={(event) => setForm({ ...form, supportsExtension: event.target.checked })}
              />
              <span className="tiny">{t('studioConnectors.slot.extension')}</span>
            </label>
          </>
        )}
      </fieldset>

      <details
        className="connector-advanced"
        open={advancedOpen}
        onToggle={(event) => setAdvancedOpen(event.currentTarget.open)}
      >
        <summary>{t('studioConnectors.advanced')}</summary>
        <p className="muted small">{t('studioConnectors.advancedHint')}</p>

        <label htmlFor={id('auth-style')}>{t('studioConnectors.authStyle')}</label>
        <select
          id={id('auth-style')}
          value={form.authStyle}
          onChange={(event) =>
            setForm({ ...form, authStyle: event.target.value as GenerationAuthStyle })
          }
        >
          {AUTH_STYLES.map((style) => (
            <option key={style} value={style}>
              {t(`studioConnectors.authStyle.${style}` as TranslationKey)}
            </option>
          ))}
        </select>

        {(form.authStyle === 'HEADER' || form.authStyle === 'QUERY') && (
          <>
            <label htmlFor={id('auth-param')}>
              {form.authStyle === 'HEADER'
                ? t('studioConnectors.headerName')
                : t('studioConnectors.queryName')}
            </label>
            <input
              id={id('auth-param')}
              className="ltr"
              dir="ltr"
              placeholder={form.authStyle === 'HEADER' ? 'x-goog-api-key' : 'key'}
              value={form.authParamName}
              onChange={(event) => setForm({ ...form, authParamName: event.target.value })}
            />
          </>
        )}

        <label htmlFor={id('template')}>{t('studioConnectors.template')}</label>
        <textarea
          id={id('template')}
          className="ltr mono"
          dir="ltr"
          rows={8}
          spellCheck={false}
          placeholder={'{\n  "model": "{{model}}",\n  "prompt": "{{prompt}}"\n}'}
          value={form.requestTemplate}
          onChange={(event) => setForm({ ...form, requestTemplate: event.target.value })}
        />
        {template.ok ? (
          <p className="muted small">{t('studioConnectors.templateHint')}</p>
        ) : (
          <p className="error small">
            {template.message
              ? t('studioConnectors.templateInvalid', { error: template.message })
              : t('studioConnectors.templateNotObject')}
          </p>
        )}

        <div className="muted small">
          <p>{t('studioConnectors.placeholders')}</p>
          <ul>
            {(['prompt', 'model', 'feedback', 'audioUrl', 'references', 'item'] as const).map(
              (name) => (
                <li key={name}>
                  <code className="ltr">{`{{${name}}}`}</code>{' '}
                  {t(`studioConnectors.placeholder.${name}` as TranslationKey)}
                </li>
              ),
            )}
          </ul>
          <p>{t('studioConnectors.placeholderEach')}</p>
          <pre className="ltr mono tiny">
            {
              '{ "$each": "references", "as": { "type": "image_url", "image_url": { "url": "{{item}}" } } }'
            }
          </pre>
          <p>{t('studioConnectors.placeholderEmpty')}</p>
        </div>

        <label htmlFor={id('response-kind')}>{t('studioConnectors.responseKind')}</label>
        <select
          id={id('response-kind')}
          value={form.responseKind}
          onChange={(event) =>
            setForm({ ...form, responseKind: event.target.value as GenerationResponseKind })
          }
        >
          {form.responseKind === '' && (
            <option value="">{t('studioConnectors.responseKind.legacy')}</option>
          )}
          {responseKinds.map((kind) => (
            <option key={kind} value={kind}>
              {t(`studioConnectors.responseKind.${kind}` as TranslationKey)}
            </option>
          ))}
        </select>

        {form.responseKind !== 'BINARY' && (
          <>
            <label htmlFor={id('response-path')}>{t('studioConnectors.responsePath')}</label>
            <input
              id={id('response-path')}
              className="ltr"
              dir="ltr"
              placeholder={pathPlaceholder}
              value={form.responsePath}
              onChange={(event) => setForm({ ...form, responsePath: event.target.value })}
            />
            <p className="muted small">{t('studioConnectors.responsePathHint')}</p>
          </>
        )}

        {form.responseKind === 'BASE64' && (
          <>
            <label htmlFor={id('response-mime')}>{t('studioConnectors.mimePath')}</label>
            <input
              id={id('response-mime')}
              className="ltr"
              dir="ltr"
              placeholder="data[0].media_type"
              value={form.responseMimePath}
              onChange={(event) => setForm({ ...form, responseMimePath: event.target.value })}
            />
            <p className="muted small">{t('studioConnectors.mimePathHint')}</p>
          </>
        )}

        {/* A provider that answers "started": where the operation id is, where
            to ask again, and what says it is finished. Empty for everyone else. */}
        <div className="connector-subgroup">
          <h4>{t('studioConnectors.polling')}</h4>
          <p className="muted small">{t('studioConnectors.pollingHint')}</p>

          <label htmlFor={id('poll-path')}>{t('studioConnectors.pollPath')}</label>
          <input
            id={id('poll-path')}
            className="ltr"
            dir="ltr"
            placeholder={POLL_EXAMPLE.pollPath}
            value={form.pollPath}
            onChange={(event) => setForm({ ...form, pollPath: event.target.value })}
          />

          <label htmlFor={id('poll-url')}>{t('studioConnectors.pollUrl')}</label>
          <input
            id={id('poll-url')}
            className="ltr"
            dir="ltr"
            placeholder={POLL_EXAMPLE.pollUrlTemplate}
            value={form.pollUrlTemplate}
            onChange={(event) => setForm({ ...form, pollUrlTemplate: event.target.value })}
          />

          <label htmlFor={id('poll-done')}>{t('studioConnectors.pollDonePath')}</label>
          <input
            id={id('poll-done')}
            className="ltr"
            dir="ltr"
            placeholder={POLL_EXAMPLE.pollDonePath}
            value={form.pollDonePath}
            onChange={(event) => setForm({ ...form, pollDonePath: event.target.value })}
          />

          <label htmlFor={id('poll-done-value')}>{t('studioConnectors.pollDoneValue')}</label>
          <input
            id={id('poll-done-value')}
            className="ltr"
            dir="ltr"
            placeholder="SUCCEEDED"
            value={form.pollDoneValue}
            onChange={(event) => setForm({ ...form, pollDoneValue: event.target.value })}
          />

          <label htmlFor={id('poll-failed-values')}>{t('studioConnectors.pollFailedValues')}</label>
          <input
            id={id('poll-failed-values')}
            className="ltr"
            dir="ltr"
            placeholder="FAILED,CANCELED,UNKNOWN"
            value={form.pollFailedValues}
            onChange={(event) => setForm({ ...form, pollFailedValues: event.target.value })}
          />
          <p className="muted small">{t('studioConnectors.pollDoneValueHint')}</p>

          <label className="check-row">
            <input
              type="checkbox"
              checked={form.authOnResultFetch}
              onChange={(event) => setForm({ ...form, authOnResultFetch: event.target.checked })}
            />
            <span className="tiny">{t('studioConnectors.authOnResultFetch')}</span>
          </label>
        </div>

        <div className="connector-subgroup">
          <h4>{t('studioConnectors.extraHeaders')}</h4>
          <p className="muted small">{t('studioConnectors.extraHeadersHint')}</p>
          <textarea
            id={id('extra-headers')}
            aria-label={t('studioConnectors.extraHeaders')}
            className="ltr mono"
            dir="ltr"
            rows={3}
            placeholder={'{ "X-DashScope-Async": "enable" }'}
            value={form.extraHeaders}
            onChange={(event) => setForm({ ...form, extraHeaders: event.target.value })}
          />
          {extraHeaders === undefined && (
            <p className="error small">{t('studioConnectors.extraHeadersInvalid')}</p>
          )}
        </div>

        <ConnectorPricingFields
          idPrefix={`connector-${uid}`}
          value={pricingForm}
          onChange={setPricingForm}
          invalid={pricingResult.invalid}
        />
      </details>

      {mode === 'edit' && initial && (
        <p className="muted small">{t('studioConnectors.editing', { name: initial.name })}</p>
      )}
      {error && <p className="error small">{error}</p>}

      <div className="actions">
        <button
          type="button"
          className="primary"
          onClick={() => save.mutate()}
          disabled={
            !form.name ||
            !form.provider ||
            !form.endpointUrl ||
            !template.ok ||
            extraHeaders === undefined ||
            clipInvalid ||
            pricingResult.invalid ||
            save.isPending
          }
        >
          {mode === 'edit' ? t('automation.agentSave') : t('studioConnectors.saveConnector')}
        </button>
        {onCancel && (
          <button type="button" className="ghost" onClick={onCancel}>
            {t('automation.agentCancel')}
          </button>
        )}
      </div>

      {mode === 'edit' && initial && (
        <>
          <p className="muted small">{t('studioConnectors.test.savedOnly')}</p>
          <ConnectorTestPanel
            connector={initial}
            paths={{
              result: form.responseKind === 'BINARY' ? null : form.responsePath,
              operation: form.pollPath,
              done: form.pollDonePath,
              mime: form.responseMimePath,
            }}
            onUsePath={usePath}
            onRepaired={(saved) => setForm(formFromRow(saved))}
          />
        </>
      )}
    </>
  );
}

/**
 * CRUD for the endpoints the Studio calls. A connector is data, not code: the
 * operator picks a preset (or starts empty) and the request/response contract
 * sits under "Advanced", where it is rarely needed once a preset fills it.
 *
 * Editing happens in place — the form opens in a full-width row directly
 * under the connector it belongs to, as the users table does — so on a laptop
 * the operator never has to find the row again at the bottom of the page.
 * The form under the table is for new connectors only.
 */
export function GenerationConnectorsPanel() {
  const queryClient = useQueryClient();
  const { t } = useI18n();

  const [editingId, setEditingId] = useState<string | null>(null);
  const [testingId, setTestingId] = useState<string | null>(null);

  const connectors = useQuery({
    queryKey: ['generation-connectors'],
    queryFn: api.listGenerationConnectors,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['generation-connectors'] });

  const remove = useMutation({
    mutationFn: api.deleteGenerationConnector,
    onSuccess: (_result, id) => {
      if (editingId === id) setEditingId(null);
      void refresh();
    },
  });

  const toggleActive = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.updateGenerationConnector(id, { isActive }),
    onSuccess: refresh,
  });

  const rows = connectors.data?.connectors ?? [];

  return (
    <>
      <p className="muted small">{t('studioConnectors.hint')}</p>

      {rows.length > 0 && (
        <div className="grid-wrap">
          <table className="grid connectors-grid">
            <colgroup>
              <col style={{ width: '10%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '9%' }} />
              <col style={{ width: '8%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '6%' }} />
              <col style={{ width: '6%' }} />
              <col style={{ width: '7%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '13%' }} />
            </colgroup>
            <thead>
              <tr>
                <th>{t('studioConnectors.name')}</th>
                <th>{t('studioConnectors.capability')}</th>
                <th>{t('studioConnectors.provider')}</th>
                <th>{t('studioConnectors.model')}</th>
                <th>{t('studioConnectors.endpoint')}</th>
                <th>{t('studioConnectors.token')}</th>
                <th>{t('studioConnectors.default')}</th>
                <th>{t('common.status')}</th>
                <th>{t('studioConnectors.verification')}</th>
                <th>{t('studioConnectors.slot.title')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((connector) => {
                const rowPreset = connector.presetId
                  ? findGenerationPreset(connector.presetId)
                  : undefined;
                const isEditing = editingId === connector.id;
                const isTesting = testingId === connector.id;
                return (
                  <Fragment key={connector.id}>
                    <tr className={isEditing ? 'is-editing' : undefined}>
                      <td className="clip" dir="auto" title={connector.name} data-primary>
                        <strong>{connector.name}</strong>
                      </td>
                      <td>
                        <span className="badge accent">
                          {t(
                            `studioConnectors.capability.${connector.capability}` as TranslationKey,
                          )}
                        </span>
                      </td>
                      <td
                        className="small clip"
                        dir="auto"
                        title={rowPreset?.label ?? connector.provider}
                      >
                        {connector.provider}
                        {rowPreset && (
                          <div className="muted tiny clip" dir="auto">
                            {rowPreset.label}
                          </div>
                        )}
                      </td>
                      <td
                        className="mono tiny clip"
                        dir="auto"
                        title={connector.model ?? undefined}
                      >
                        {connector.model || '—'}
                        <PriceTag pricing={connector.pricing} />
                      </td>
                      <td className="mono tiny clip" dir="auto" title={connector.endpointUrl}>
                        {connector.endpointUrl}
                      </td>
                      <td className="small">
                        {connector.hasAuthToken ? t('automation.agentStored') : '—'}
                      </td>
                      <td>{connector.isDefault ? <Icon.check /> : '—'}</td>
                      <td>
                        <button
                          type="button"
                          className={`badge ${connector.isActive ? 'ok' : 'warn'}`}
                          style={{ border: 'none', cursor: 'pointer' }}
                          onClick={() =>
                            toggleActive.mutate({ id: connector.id, isActive: !connector.isActive })
                          }
                        >
                          {connector.isActive
                            ? t('studioConnectors.active')
                            : t('studioConnectors.inactive')}
                        </button>
                      </td>
                      <td>
                        <VerificationBadge status={connector.verificationStatus} />
                      </td>
                      <td>
                        <SlotTags connector={connector} />
                      </td>
                      <td className="row-actions">
                        <button
                          type="button"
                          className="ghost small"
                          aria-expanded={isTesting}
                          onClick={() => setTestingId(isTesting ? null : connector.id)}
                        >
                          {t('studioConnectors.test.button')}
                        </button>
                        <button
                          type="button"
                          className="ghost small"
                          aria-expanded={isEditing}
                          onClick={() => setEditingId(isEditing ? null : connector.id)}
                        >
                          {isEditing ? t('common.close') : t('common.edit')}
                        </button>
                        <button
                          type="button"
                          className="danger small"
                          onClick={() => remove.mutate(connector.id)}
                        >
                          <Icon.trash />
                        </button>
                      </td>
                    </tr>
                    {isTesting && !isEditing && (
                      <tr className="detail-row connector-edit-row">
                        <td colSpan={COLUMN_COUNT} data-primary>
                          <div className="inline-edit connector-inline-edit">
                            <ConnectorTestPanel key={connector.id} connector={connector} />
                          </div>
                        </td>
                      </tr>
                    )}
                    {/* Under the row it belongs to rather than in the add form
                        at the bottom: the operator pressed Edit here. */}
                    {isEditing && (
                      <tr className="detail-row connector-edit-row">
                        <td colSpan={COLUMN_COUNT} data-primary>
                          <div className="inline-edit connector-inline-edit">
                            <h3>{t('studioConnectors.edit')}</h3>
                            <ConnectorForm
                              key={connector.id}
                              mode="edit"
                              initial={connector}
                              onSaved={() => {
                                setEditingId(null);
                                void refresh();
                              }}
                              onCancel={() => setEditingId(null)}
                            />
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="card composer">
        <h3>{t('studioConnectors.add')}</h3>
        <ConnectorForm mode="create" onSaved={() => void refresh()} />
      </div>
    </>
  );
}
